/**
 * main.ts
 *
 * Bootstraps Vuetify and other plugins then mounts the App`
 */

// Styles
import "@/styles/main.scss";

// Plugins
import { registerPlugins } from "@/plugins";
import axios from 'axios'

// Mock data
import { 
  mockSubjects, 
  mockFormats, 
  mockGrades, 
  mockFilterGroups, 
  mockSeriesOverview, 
  mockSeriesDetail, 
  mockReportOverview, 
  mockGatewayDetail 
} from '@/mocks/api-data';

// Components
import App from './App.vue';

// Composables
import { createApp } from 'vue';

// Store
import { useReportsStore } from '@/store';

// Create app instance
const app = createApp(App);

// Global properties
app.config.globalProperties.axios = axios;

// Register plugins
registerPlugins(app);

// Initialize the reports store immediately before mounting the app
const reportsStore = useReportsStore();

// Don't use setTimeout for initialization, do it immediately
// console.log('Initializing reports store...');

// Initialize the store with a fall-back
const initializeStore = async () => {
  try {
    // Try initializing through the store's method
    // console.log('Starting store initialization');
    await reportsStore.initialize();
    // console.log('Store initialization complete');
  } catch (error) {
    console.error('Store initialization failed, using direct calls...', error);
    
    // Set up default subject
    if (reportsStore.selectedSubjects.length === 0) {
      reportsStore.selectedSubjects.push('ELA');
    }

    // Try direct API calls with minimal error handling
    try {
      // Get the API URL from environment or use default
      const apiUrl = import.meta.env.VITE_APP_API_URL || 'https://api.dev.edreports.org';
      
      // Fetch filters directly
      // console.log('Fetching filters directly...');
      const filtersResponse = await fetch(`${apiUrl}/reports/filters`);
      const filtersData = await filtersResponse.json();
      
      if (filtersData.items) {
        reportsStore.filters = filtersData.items.map((filter: any) => ({
          id: filter.slug || filter.id,
          name: filter.label || filter.name,
          options: filter.options.map((option: any) => ({
            value: option.value,
            label: option.label || option.name || option.value
          }))
        }));
        // console.log(`Loaded ${reportsStore.filters.length} filters directly`);
      }
      
      // Fetch reports directly 
      // console.log('Fetching reports directly...');
      const reportsResponse = await fetch(`${apiUrl}/reports`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          filters: {
            subject: reportsStore.selectedSubjects
          },
          limit: 10,
          offset: 0
        })
      });
      
      const reportsData = await reportsResponse.json();
      if (reportsData.items) {
        reportsStore.reports = reportsData.items;
        reportsStore.totalItems = reportsData.total || 0;
        // console.log(`Loaded ${reportsStore.reports.length} reports directly`);
      }
    } catch (directError) {
      console.error('Direct API calls failed:', directError);
    }
  }
};

// Run initialization immediately
initializeStore().then(() => {
  // Mount app after initialization attempt
  // console.log('Mounting app');
  app.mount('#app');
}).catch(error => {
  console.error('Fatal initialization error:', error);
  // Mount app anyway so it's not completely broken
  app.mount('#app');
});
